import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { DelegatedTeam, StudentRoleSettings } from 'src/app/auth/models/UserData';
import { AuthService } from 'src/app/auth/services/auth.service';
import { CustomFieldKind } from 'src/app/modules/personnel-files/custom-fields/enums/CustomFieldKind';
import { CustomFieldTab } from 'src/app/modules/personnel-files/custom-fields/models/CustomFieldTab';
import { CustomFieldService } from 'src/app/modules/personnel-files/custom-fields/services/custom-field.service';
import { MenuItem } from 'src/app/shared/framework/navigation/models/MenuItem';
import { GeneralActor } from 'src/app/shared/general-enums/GeneralActor';
import { GeneralStatus } from 'src/app/shared/general-enums/GeneralStatus';
import { Employee } from '../../../shared/personnel-files/models/Employee';
import { EmployeeService } from '../../../shared/personnel-files/services/employee.service';

@Component({
  selector: 'app-employee-tabs',
  templateUrl: './employee-tabs.component.html',
  styleUrls: ['./employee-tabs.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EmployeeTabsComponent implements OnInit, OnDestroy {
  public Roles = GeneralActor;

  public tabs: MenuItem[] = [];
  public customFieldTabs: CustomFieldTab[] = [];

  public level1Tabs: MenuItem[] = [];
  public level2Tabs: MenuItem[] = [];
  public level3Tabs: MenuItem[] = [];

  public selectedLevel1?: MenuItem;
  public selectedLevel2?: MenuItem;
  public selectedLevel3?: MenuItem;

  public activeLevel1Id?: number;
  public activeLevel2Id?: number;
  public activeLevel3Id?: number;

  public shownTab?: MenuItem;

  public student: Employee;
  public studentId: number;
  public iframelink: string;
  public iframelinkSafe: SafeResourceUrl;
  public loading = true;
  public initialised = false;
  public employeelistIframe: SafeResourceUrl;
  public role: GeneralActor = GeneralActor.Employee;

  @ViewChild('iframe') iframe: ElementRef<HTMLIFrameElement>;
  @ViewChild('employeeListIframe') employeeListIframe?: ElementRef<HTMLIFrameElement>;
  @ViewChild('contentframe') contentframe: ElementRef<HTMLDivElement>;

  @Input() conversationStudentId: number = null;
  @Input() isConversation: boolean = false;


  private subscriptions: Subscription[] = [];
  private queryStringParams: { key: string, value: string }[] = [];

  constructor(
    private customFieldService: CustomFieldService,
    private studentService: EmployeeService,
    private authService: AuthService,
    private route: ActivatedRoute,
    private sanitizer: DomSanitizer,
    private _change: ChangeDetectorRef
  ) {
    // WATERFALL SYSTEM
    if (localStorage.getItem('corporatehr_view') === '1') {
      this.role = GeneralActor.CorporateHR;
    } else if (localStorage.getItem('localhr_view') === '1') {
      this.role = GeneralActor.LocalHR;
    } else if (localStorage.getItem('delegate_view') === '1') {
      this.role = GeneralActor.Delegate;
    } else if (localStorage.getItem('matrixmanager_view') === '1') {
      this.role = GeneralActor.Matrix;
    } else if (localStorage.getItem('manager_view') === '1') {
      this.role = GeneralActor.Manager;
    } else if (localStorage.getItem('candidate_view') === '1') {
      this.role = GeneralActor.Candidate;
    } else {
      this.role = GeneralActor.Employee;
    }
  }

  async ngOnInit() {

    let obs: Observable<any>[] = [
      this.route.queryParamMap,
      this.customFieldService.getEmployeeTabs(this.role),
      this.customFieldService.getCustomFields(),
      this.authService.getUserRoles(true)
    ];

    this.subscriptions.push(
      combineLatest([...obs])
        .subscribe(([queryParam, tabs, customFields, userRoles]) => {
          let menuId = parseInt(queryParam.get('menu') ?? this.route.snapshot.data['menuId']);
          let studentId: number;

          if (this.isConversation) {
            studentId = this.conversationStudentId;
          } else {
            studentId = Number(queryParam.get('item'));
          }

          if (studentId && this.studentId !== studentId) {
            this.studentId = studentId;
            this.getStudent();
          } else if (!this.isConversation && queryParam.has('employee')) {
            this.studentId = undefined;
            this.student = undefined;
            this.iframelink = undefined;
            this.iframelinkSafe = undefined;
          }

          this.queryStringParams = queryParam.keys
            .filter(key =>
              key !== 'menu' &&
              key.toLowerCase() !== 'employeeid'
            )
            .map(key => ({ key, value: queryParam.get(key) }));

          let baseUrl = '';

          switch (this.role) {
            case GeneralActor.CorporateHR:
            case GeneralActor.LocalHR:
              baseUrl = '/perfnav/administration/en/common/students/searchresults.asp';
              break;

            case GeneralActor.Manager:
            case GeneralActor.Delegate:
            case GeneralActor.Matrix:
            case GeneralActor.N2:
              baseUrl = '/perfnav/student/ML/sharedmenus/mainpages/teamhome.asp';
              break;
          }

          const params = new URLSearchParams();
          params.set('employeeid', studentId ? String(studentId) : '');
          params.set('item', studentId ? String(studentId) : '');
          params.set('dotnet', '1');

          if (this.role == GeneralActor.Delegate) {
            let activeDelegation: DelegatedTeam = this.authService.getActiveDelegation();
            let delegatedMgrId = activeDelegation.studentId ?? null;
            if (delegatedMgrId) {
              params.set('delegate', '1');
              params.set('studentid', String(delegatedMgrId));
            }
          }

          if (this.role == GeneralActor.Employee) {
            params.set('DOTNET', '2');
            params.set('myinfo', '1');
            params.set('extra', '1');
          }

          for (const { key, value } of this.queryStringParams) {
            if (value && !params.has(key)) {
              params.set(key, value);
            }
          }

          this.employeelistIframe = this.sanitizer.bypassSecurityTrustResourceUrl(baseUrl + '?' + params.toString());

          for (const key of params.keys()) {
            const value = params.get(key);
            this.upsertQueryParam(key, value);
          }

          const redirect = queryParam.get('redirect')?.toLowerCase();
          if (!menuId && redirect) {
            menuId = REDIRECT_TO_MENU[redirect];
          }
          if (!menuId) {
            menuId = 1;
          }


          const mappedCustomTabs = this.mapCustomTabs(customFields);

          this.tabs = [
            ...tabs,
            ...mappedCustomTabs
          ];

          this.level1Tabs = this.getChildren(0);
          this.selectLevel1(this.level1Tabs[0]);

          if (menuId) {
            const menu: MenuItem[] = [];
            let tab = this.tabs.filter(e => e.id == menuId)[0];
            if (!tab) {
              this._change.markForCheck();
              return;
            }
            menu.unshift(tab);

            if (tab.parentID) {
              let parent = this.tabs.filter(e => e.id == tab.parentID)[0];
              if (parent) {
                menu.unshift(parent);

                if (parent.parentID) {
                  let parentOfParent = this.tabs.filter(e => e.id == parent.parentID)[0];
                  if (parentOfParent) {
                    menu.unshift(parentOfParent);
                  }
                }
              }
            }

            if (menu.length == 1) {
              this.selectLevel1(menu[0]);
            } else {
              for (let i = 1; i <= menu.length; i++) {
                const m = menu[i - 1];

                switch (i) {
                  case 1:
                    this.selectLevel1(m, menu.length > 1);
                    break;
                  case 2:
                    this.selectLevel2(m, menu.length > 2);
                    break;
                  case 3:
                    this.selectLevel3(m);
                    break;
                }
              }
            }
          }

          this._change.markForCheck();
        })
    );


    window.addEventListener('message', this.onMessage);
    this.initialised = true;
  }

  @HostListener('window:resize')
  resizeFrame() {
    setTimeout(() => {
      try {
        (<any>window.top).initIframeResize();
        if (window.document.body) {
          window.document.body.style.height = "100%";
        }


      } catch (e) { }
    }, 500);
  }

  public selectLevel1(tab: MenuItem, skipUrlConstruction = false) {
    this.selectedLevel1 = tab;
    this.activeLevel1Id = tab.id;

    this.selectedLevel2 = undefined;
    this.selectedLevel3 = undefined;
    this.activeLevel2Id = undefined;
    this.activeLevel3Id = undefined;
    this.level3Tabs = [];

    this.level2Tabs = this.getChildren(tab.id);

    if (!skipUrlConstruction) {
      if (this.level2Tabs.length > 0) {
        const firstLevel2 = this.level2Tabs[0];

        this.selectedLevel2 = firstLevel2;
        this.activeLevel2Id = firstLevel2.id;

        this.level3Tabs = this.getChildren(firstLevel2.id);

        if (this.level3Tabs.length > 0) {
          this.activeLevel3Id = this.level3Tabs[0].id;
        }
      } else {
        this.selectedLevel2 = undefined;
        this.selectedLevel3 = undefined;
        this.level3Tabs = [];
        this.activeLevel2Id = undefined;
        this.activeLevel3Id = undefined;
      }

      if (this.selectedLevel3) {
        this.selectMenuItem(this.selectedLevel3);
      } else if (this.selectedLevel2) {
        this.selectMenuItem(this.selectedLevel2);
      } else {
        this.selectMenuItem(tab);
      }
    }
    this._change.markForCheck();
  }

  public selectLevel2(tab: MenuItem, skipUrlConstruction = false) {
    this.selectedLevel2 = tab;
    this.activeLevel2Id = tab.id;

    this.selectedLevel3 = undefined;
    this.activeLevel3Id = undefined;

    this.level3Tabs = this.getChildren(tab.id);

    if (!skipUrlConstruction) {
      if (this.level3Tabs.length > 0) {
        this.selectedLevel3 = this.level3Tabs[0];
        this.activeLevel3Id = this.level3Tabs[0].id;
      } else {
        this.activeLevel3Id = undefined;
      }


      if (this.selectedLevel3) {
        this.selectMenuItem(this.selectedLevel3);
      } else {
        this.selectMenuItem(this.selectedLevel2);
      }
    }
    this._change.markForCheck();
  }

  public selectLevel3(tab: MenuItem) {
    this.selectedLevel3 = tab;
    this.activeLevel3Id = tab.id;

    this.selectMenuItem(this.selectedLevel3);
  }

  public trackTabs(index: number, item: MenuItem) {
    return item.id;
  }

  public onIframeLoad() {
    this.loading = false;

    try {
      const doc = this.iframe.nativeElement.contentDocument;

      if (this.shownTab.url?.toLowerCase().includes('perfnav')) {
        const body = doc?.body;
        const html = doc?.documentElement;

        const height = Math.max(
          body?.scrollHeight ?? 0,
          body?.offsetHeight ?? 0,
          html?.scrollHeight ?? 0,
          html?.offsetHeight ?? 0,
          html?.clientHeight ?? 0,
        );

        if (height && this.contentframe?.nativeElement) {
          this.contentframe.nativeElement.style.height = (height + 20) + 'px';
          this.iframe.nativeElement.style.height = height + 'px';
        }
      }
    } catch (e) { }
  }

  ngOnDestroy(): void {
    window.removeEventListener('message', this.onMessage);

    for (const subsc of this.subscriptions) {
      subsc.unsubscribe();
    }
  }

  private getStudent() {
    const requestedStudentId = this.studentId;

    this.subscriptions.push(
      this.studentService.getEmployeeLean(requestedStudentId).subscribe(e => {
        if (this.studentId !== requestedStudentId) {
          return;
        }

        this.student = e;

        this._change.markForCheck();
      })
    );
  }

  private getChildren(parentId: number): MenuItem[] {
    return this.tabs.filter(t => t.parentID === parentId).sort((a, b) => a.sortOrder - b.sortOrder);
  }

  private mapCustomTabs(customTabs: CustomFieldTab[]): MenuItem[] {
    return customTabs.filter(t =>
      (
        t.customFieldKind == CustomFieldKind.ShowOnEmployeeFiche ||
        t.customFieldKind == CustomFieldKind.ShowOnEmployeeAndCandidateFiche
      ) &&
      t.status == GeneralStatus.Active &&
      t.categories.some(c => c.status == GeneralStatus.Active && c.fields.some(f => f.status == GeneralStatus.Active))
    ).map(t => ({
      id: t.customFieldTabId + 1000000, // avoid collision with normal ids
      parentID: t.parentMenuId ?? 0,
      categoryID: 1,
      sortOrder: t.sortOrder,
      url: null,
      label: t.name,
      pageNumber: null,
      showTaskCounter: false,
      cssClass: null,
      crmModuleIds: null,
      isMenuItemContainerWithoutPage: false,
      hideInNavigation: false,
      subPages: [],
      getAngularUrl: null,

      // extra info so we know this is a custom tab
      isCustom: true,
      customFieldTab: t
    }));
  }

  private onMessage = (event: MessageEvent) => {
    if (event.data?.action === 'switchEmployee') {
      if (event.origin !== window.location.origin) {
        return;
      }

      const allowedSources = [
        this.iframe?.nativeElement.contentWindow,
        this.employeeListIframe?.nativeElement.contentWindow
      ];

      if (!allowedSources.includes(event.source as Window)) {
        return;
      }

      const switchedFromEmployeeList = event.source === this.employeeListIframe?.nativeElement.contentWindow;

      const prevStudentId = this.studentId;
      const newStudentId = Number(event.data.studentId);

      if (!Number.isInteger(newStudentId) || newStudentId <= 0 || prevStudentId === newStudentId) {
        return;
      }

      // Rebuild all forwarded params from the message
      const forwarded: Record<string, string> = {
        item: String(newStudentId),
        cy: event.data.companyId ?? '',
        companyid: event.data.companyId ?? '',
        indirrep: event.data.indirrep ?? '',
        dottedrep: event.data.dottedrep ?? '',
        delegate: event.data.delegate ? '1' : '',
        studentid: event.data.delegateID ?? '',
        mgrscope: event.data.mgrscope ?? '',
        studid: String(newStudentId),
        employeeid: String(newStudentId),
        extra: event.data.extra ?? '',
        myinfo: event.data.myinfo ?? '',
        fillvar: event.data.fillvar ?? '',
        actor: event.data.actor ?? '',
      };

      for (const [key, value] of Object.entries(forwarded)) {
        if (value !== '' && value !== 'null' && value !== 'undefined') {
          this.upsertQueryParam(key, value);
        } else {
          this.removeQueryParam(key);
        }
      }

      this.studentId = newStudentId;
      this.getStudent();
      this._change.markForCheck();

      if (!switchedFromEmployeeList) {
        this.constructEmployeeListUrl();
      }
      this.constructUrl();
    }

    if (event.data?.action === 'iframeHeight') {
      const height = event.data.height;
      if (this.contentframe?.nativeElement && height) {
        this.contentframe.nativeElement.style.height = (height + 20) + 'px';
        this.iframe.nativeElement.style.height = height + 'px';
      }
    }
  }

  private upsertQueryParam(key: string, value: string) {
    const existing = this.queryStringParams.find(p => p.key === key);
    if (existing) {
      existing.value = value;
    } else {
      this.queryStringParams.push({ key, value });
    }
  }

  private removeQueryParam(key: string) {
    this.queryStringParams = this.queryStringParams.filter(p => p.key !== key);
  }

  private constructEmployeeListUrl() {
    let baseUrl = '';

    switch (this.role) {
      case GeneralActor.CorporateHR:
      case GeneralActor.LocalHR:
        baseUrl = '/perfnav/administration/en/common/students/searchresults.asp';
        break;

      case GeneralActor.Manager:
      case GeneralActor.Delegate:
      case GeneralActor.Matrix:
      case GeneralActor.N2:
        baseUrl = '/perfnav/student/ML/sharedmenus/mainpages/teamhome.asp';
        break;
    }

    if (!baseUrl || !this.studentId) {
      return;
    }

    const params = new URLSearchParams();
    params.set('employeeid', String(this.studentId));
    params.set('item', String(this.studentId));
    params.set('dotnet', '1');

    if (this.role == GeneralActor.Delegate) {
      const delegatedMgrId = this.authService.getActiveDelegation().studentId ?? null;
      if (delegatedMgrId) {
        params.set('delegate', '1');
        params.set('studentid', String(delegatedMgrId));
      }
    }

    for (const { key, value } of this.queryStringParams) {
      if (value && !params.has(key)) {
        params.set(key, value);
      }
    }

    this.employeelistIframe = this.sanitizer.bypassSecurityTrustResourceUrl(baseUrl + '?' + params.toString());
  }

  private selectMenuItem(tab: MenuItem) {
    this.shownTab = tab;

    if (tab.url) {
      this.constructUrl();
    } else if (tab.customFieldTab) {
      // this.shownTab.url = '/personnel-files/custom-field-content/' + tab.customFieldTab.customFieldTabId;
      // this.constructUrl();
    }
  }

  private constructUrl() {
    let url = this.shownTab?.url;
    if (!url) return;

    if (!this.studentId) return;

    url = url.replace('{studentid}', this.studentId?.toString());

    const [base, existingQs] = url.split('?');
    const existing = new URLSearchParams(existingQs ?? '');

    for (const { key, value } of this.queryStringParams) {
      if (!existing.has(key)) {
        existing.set(key, value);
      }
    }



    if (!existing.has('employeeid')) {
      existing.set('employeeid', String(this.studentId));
    }
    if (!existing.has('item')) {
      existing.set('item', String(this.studentId));
    }
    existing.set('dotnet', '1');

    if (this.isConversation && this.role === GeneralActor.Employee) {
      // Prevent myinfo.asp from redirecting back to this component inside the iframe.
      existing.set('showmyinfo', '1');
    }

    if (this.role == GeneralActor.Delegate) {
      let activeDelegation: DelegatedTeam = this.authService.getActiveDelegation();
      let delegatedMgrId = activeDelegation.studentId ?? null;
      if (delegatedMgrId) {
        if (!existing.has('delegate')) {
          existing.set('delegate', '1');
        }
        if (!existing.has('studentid')) {
          existing.set('studentid', String(delegatedMgrId));
        }
      }
    }

    // Remove unfilled template placeholders
    for (const [key, value] of existing.entries()) {
      if (value.includes('{')) existing.delete(key);
    }

    // Add this block right after:
    for (const [key, value] of existing.entries()) {
      if (value === 'null' || value === 'undefined') existing.delete(key);
    }

    url = base + '?' + existing.toString();

    if (this.iframelink !== url) {
      this.loading = true;
      this.iframelink = url;
      this.iframelinkSafe = this.sanitizer.bypassSecurityTrustResourceUrl(url);
    }

    this._change.markForCheck();
  }
}

const REDIRECT_TO_MENU: Record<string, number> = {
  '/perfnav/administration/en/common/students/viewstudentdetailgeneral.asp': 1,
  '/perfnav/administration/en/common/students/viewstudentdetailcertificates.asp': 3,
  '/perfnav/administration/en/common/students/viewstudentdetailobjectives.asp': 3,
  '/perfnav/administration/en/common/students/viewstudentdetailsalary.asp': 10,
  '/perfnav/administration/en/common/students/viewstudentattachments.asp': 11,
  '/perfnav/administration/en/common/students/viewstudentdetailcompetenciestech.asp': 12,
  '/perfnav/student/ml/common/schedule/viewscheduledetailgeneral.asp': 15,
  '/perfnav/student/ml/common/schedule/viewcourserequestsdetailgeneral.asp': 16,
  '/perfnav/student/ml/common/history/viewhistorydetailgeneral.asp': 17,
  '/perfnav/administration/en/common/students/viewadhocconversations.asp': 36,
  '/development-objectives/employee-development-objectives': 38,
  '/perfnav/administration/en/common/students/viewstudentdetaildevelopment.asp': 38,
  '/perfnav/administration/en/common/students/viewstudentdetailreviews.asp': 39,
  '/perfnav/student/ml/common/careermanagement/careerstart.asp': 64,
  '/perfnav/student/ml/common/careermanagement/education.asp': 65,
  '/perfnav/student/ml/common/careermanagement/career.asp': 66,
  '/perfnav/student/ml/common/careermanagement/knowledge.asp': 67,
  '/perfnav/student/ml/common/careermanagement/careerperspectives.asp': 68,
  '/perfnav/student/ml/common/performance/viewgeneralcomment.asp': 90,
  '/perfnav/administration/en/common/students/viewstudentskills.asp': 295,
  '/perfnav/administration/en/common/waitlist/approvedncc.asp': 297,
  '/perfnav/administration/en/common/students/viewstudentdetailfla.asp': 1001,
  '/perfnav/administration/en/common/students/viewstudentworkroster.asp': 1078,
  '/perfnav/administration/en/common/students/viewstudentabsencequotas.asp': 1079,
};
